import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Autocomplete as MuiAutocomplete, IconButton, MenuItem, SelectChangeEvent, TextField } from '@mui/material';
import { CoreBox, CoreDialog, CoreDialogContent, CoreTypography } from '@youscience/core';
import { majorsDescriptionMap, searchFilters, SortDirection } from '@common-types';
import { uniqBy, omit } from 'lodash-es';

import useHomePageStore from '@store/HomePageStore';

import { IPEDS } from '@components/IPEDS';
import { renderNoOptionsText } from '@components/Form/Autocomplete/Autocomplete';

import { ADD_MODE_DESCRIPTION, EDIT_MODE_DESCRIPTION, SORT_BY_OPTIONS } from '@constants/adminHomePage';

import { Completion, MajorData as MajorDataProps, SaveData, SortByValues } from '@interfaces/adminHomePage';

import { Option } from '@interfaces';

import CloseIcon from '@images/drawer-close.svg?react';
import EmptyStateIcon from '@images/empty-state.svg?react';
import SortByIcon from '@images/sort.svg?react';

import MajorData from './components/MajorData';

import {
  StyledCancelBtn,
  StyledDialogTitle,
  StyledFooter,
  StyledSaveBtn,
  StyledSearchResults,
  StyledSortByWrap,
  StyledSortSelect,
  sxStyles,
} from './AreaModal.styles';

interface AreaModalProps {
  isOpenAreaModal: boolean;
  editModeCipCode: number | null;
  onCloseAreaModal: () => void;
}

const ARRAY_OF_MAJORS = Array.from(majorsDescriptionMap);

export const AreaModal = (props: AreaModalProps) => {
  const { isOpenAreaModal, editModeCipCode, onCloseAreaModal } = props;

  const editModeAreaOption = useMemo(() => {
    const option = searchFilters.areasOfStudy.filters.find((item) => item?.value && +item.value === editModeCipCode);

    return option || null;
  }, [editModeCipCode]);

  const [selectedArea, setSelectedArea] = useState<Option | null>(editModeAreaOption as Option);
  const [sortBy, setSortBy] = useState<SortByValues>({ sort: 'CIP', sortDirection: SortDirection.ASC });
  const [saveData, setSaveData] = useState({} as SaveData);
  const [disableData, setDisableData] = useState({} as SaveData);

  const { completions, directory } = useHomePageStore((state) => state.adminHomePageData.school);

  const updateAdminHomePageData = useHomePageStore((state) => state.updateAdminHomePageData);

  const handleMajorDataChange = (
    cipCode: string,
    awLevel: number[] | null,
    additionalParams: { shouldOmit?: boolean },
  ) => {
    setSaveData((prevState) => {
      return { ...prevState, [cipCode]: { awLevel, additionalParams } };
    });

    setDisableData((prevState) => {
      if (awLevel?.length === 0 && additionalParams.shouldOmit) {
        return { ...omit(prevState, cipCode) };
      }

      return { ...prevState, [cipCode]: { awLevel, additionalParams } };
    });
  };

  const handleSaveData = async () => {
    const preparedCompletion: Completion[] = Object.entries(saveData).map(([key, value]) => ({
      cipCode: key,
      awLevel: value.awLevel?.length ? value.awLevel : null,
    }));

    await updateAdminHomePageData(
      { completions: uniqBy([...preparedCompletion, ...completions], 'cipCode') },
      'School areas of study and majors updated',
    );

    onCloseAreaModal();
  };

  const modalTitle = editModeCipCode
    ? `Select majors related to ${editModeAreaOption?.label} area of study`
    : `Add area of study for your ${directory.name}`;

  const handleSelectArea = (event: SyntheticEvent, area: Option | null) => {
    setSelectedArea(area as Option);
  };

  const handleChangeSort = (event: SelectChangeEvent<unknown>) => {
    const [sort, sortDirection] = (event.target.value as string).split(',') as [string, SortDirection];

    setSortBy({ sort, sortDirection });
  };

  const majorsByCipCode = useMemo(() => {
    if (!selectedArea?.value) return [];

    return ARRAY_OF_MAJORS.reduce((acc: MajorDataProps[], [cipCode, majorData]) => {
      if (majorData.cipFamily === selectedArea?.value) {
        return [...acc, majorData];
      }

      return [...acc];
    }, [] as MajorDataProps[]);
  }, [selectedArea]);

  const sortedMajors = useMemo(() => {
    if (sortBy.sort === 'CIP') {
      if (sortBy.sortDirection === SortDirection.ASC) {
        return majorsByCipCode.sort((a, b) => +a.cipCode - +b.cipCode);
      }
      return majorsByCipCode.sort((a, b) => +b.cipCode - +a.cipCode);
    }

    if (sortBy.sort === 'Name') {
      if (sortBy.sortDirection === SortDirection.ASC) {
        return majorsByCipCode.sort((a, b) => a.cipTitle.localeCompare(b.cipTitle));
      }
      return majorsByCipCode.sort((a, b) => b.cipTitle.localeCompare(a.cipTitle));
    }
  }, [sortBy, majorsByCipCode]);

  const isDisabledSave =
    Object.values(disableData).some((value) => !value?.awLevel?.length) || !Object.values(disableData).length;

  useEffect(() => {
    const initialSaveData = completions.reduce((acc, item) => {
      if (Math.floor(+item.cipCode).toString() === selectedArea?.value) {
        return {
          ...acc,
          [item.cipCode]: {
            awLevel: item.awLevel,
            additionalParams: {},
          },
        };
      }

      return acc;
    }, {} as SaveData);

    setSaveData(initialSaveData);
    setDisableData(initialSaveData);
  }, [selectedArea]);

  return (
    <CoreDialog sx={sxStyles.dialog} open={isOpenAreaModal} fullScreen scroll="paper">
      <StyledDialogTitle data-testid="areaModalTitle">
        {modalTitle}

        <IconButton onClick={onCloseAreaModal}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <CoreDialogContent sx={sxStyles.contentWrap}>
        <CoreBox sx={sxStyles.contentInner}>
          <CoreTypography variant="h5" component="h4">
            {editModeCipCode ? editModeAreaOption?.label : 'Areas of study'}
          </CoreTypography>

          <CoreTypography sx={sxStyles.description}>
            {editModeCipCode ? EDIT_MODE_DESCRIPTION : ADD_MODE_DESCRIPTION}
          </CoreTypography>

          {editModeCipCode ? (
            <CoreTypography sx={sxStyles.accordingMajorsTitle} variant="h6" component="h5">
              Majors according to the data provided by IPEDS
            </CoreTypography>
          ) : (
            <CoreTypography variant="h6" component="h5">
              Areas of study according to the data provided by IPEDS
            </CoreTypography>
          )}

          {!editModeCipCode ? (
            <MuiAutocomplete
              data-testid="areasAutocomplete"
              sx={sxStyles.autocomplete}
              size="small"
              value={selectedArea}
              options={searchFilters.areasOfStudy.filters as Option[]}
              renderInput={(params) => <TextField {...params} label="Select area of study" />}
              clearIcon={null}
              noOptionsText={renderNoOptionsText()}
              onChange={handleSelectArea}
            />
          ) : null}

          <StyledSortByWrap>
            <SortByIcon />

            <CoreTypography variant="h6" component="h5">
              Sort by:
            </CoreTypography>

            <StyledSortSelect
              data-testid="sort"
              value={`${sortBy.sort},${sortBy.sortDirection}`}
              onChange={handleChangeSort}
            >
              {SORT_BY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledSortSelect>
          </StyledSortByWrap>

          <StyledSearchResults>
            {selectedArea ? (
              <CoreBox sx={sxStyles.majorsWrap}>
                {sortedMajors?.map(({ cipCode }, index) => (
                  <MajorData key={cipCode + index} cipCode={cipCode} onMajorDataChange={handleMajorDataChange} />
                ))}
              </CoreBox>
            ) : (
              <CoreBox sx={sxStyles.emptyState}>
                <EmptyStateIcon />

                <CoreTypography>Select area of study or search for major / CIP code</CoreTypography>
              </CoreBox>
            )}
          </StyledSearchResults>

          <CoreBox sx={sxStyles.ipedsBg}>
            <IPEDS />
          </CoreBox>
        </CoreBox>
      </CoreDialogContent>

      <StyledFooter>
        <StyledCancelBtn onClick={onCloseAreaModal}>Cancel</StyledCancelBtn>

        <StyledSaveBtn disabled={isDisabledSave} onClick={handleSaveData}>
          Save
        </StyledSaveBtn>
      </StyledFooter>
    </CoreDialog>
  );
};
